import axios, { AxiosResponse } from 'axios'
import React, { useEffect, useRef, useState } from 'react'
import { IntlShape, useIntl } from 'react-intl'
import styled from 'styled-components'
import {
  AutoliittoSeparatorLine,
  ButtonContainer,
} from '../../containers/TowingRecordPage/components/routes/subforms/styled'
import { Button } from '../Button'
import { BigButtonSection, Section, Text } from '../layout'
import { Modal } from '../Modal'
import { FullscreenModal } from '../ModalPage'
import { setErrorNotification, setSuccessNotification, setUnauthorizedNotification } from '../notification'
import { setGenericErrorNotification } from '../notification/genericResponseErrorNotification'
import { Loading } from '../responses/Loading'
import { messages as buttonMessages } from '../Button/messages'
import { imagesMessages } from './messages'
import { Thumbnail } from './Thumbnail'
import { TowingRecordFile, TowingRecordFileResponse } from './types'
import { axiosInstance } from '../../http-client'
import { isRunningInWebview } from '../../graphql-client/storedState'

const FILES_PER_UPLOAD = 10

interface ImagesProps {
  onClose: () => void
  towingRecordId: number
  vehicleRegNo?: string
}

export const Images: React.FC<ImagesProps> = ({ onClose, towingRecordId, vehicleRegNo }) => {
  const intl = useIntl()
  const [images, setImages] = useState<TowingRecordFile[]>([])
  const [pendingFiles, setPendingFiles] = useState<Map<string, File>>(new Map())
  const [loading, setLoading] = useState<boolean>(false)
  const [imageToDelete, setImageToDelete] = useState<TowingRecordFile | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const cameraInputRef = useRef<HTMLInputElement>(null)

  const onImageChange = async (e: any) => {
    const files: File[] = Array.from(e.target.files)
    const newImages = files.map((f: File) => {
      return {
        id: null,
        type: 'photo',
        uuid: '',
        description: '',
        url: URL.createObjectURL(f),
        isPublic: false,
        mimeType: '',
        extension: '',
        base64: '',
        createdAt: null,
      } as TowingRecordFile
    })

    setPendingFiles(prev => {
      const next = new Map(prev)
      files.forEach((file, i) => next.set(newImages[i].url, file))
      return next
    })
    setImages(prev => [...prev, ...newImages])

    e.target.value = ''
  }

  const handleUpload = async () => {
    if (images.length > FILES_PER_UPLOAD) {
      setErrorNotification(
        intl.formatMessage(imagesMessages.uploadErrorTitle),
        intl.formatMessage(imagesMessages.tooManyFiles, { limit: FILES_PER_UPLOAD, count: images.length }),
      )
      return
    }

    try {
      if (pendingFiles.size > 0) {
        setLoading(true)
        const data = new FormData()

        for (const file of pendingFiles.values()) {
          data.append(file.name, file)
        }

        const response = await upload(data, towingRecordId)

        handleResponse(response, intl)

        const images = response.data.data.map((f: TowingRecordFile) => {
          const blob = b64toBlob(f.base64, f.mimeType)
          const blobUrl = URL.createObjectURL(blob)
          f.url = blobUrl

          return f
        })
        setImages(images)
        setPendingFiles(new Map())
      }
    } catch (e: any) {
      const message = e && e.message ? e.message : '';
      console.error(message)
      setErrorNotification(intl.formatMessage(imagesMessages.fetchFailedTitle), intl.formatMessage(imagesMessages.uploadFailed))
    } finally {
      setLoading(false)
    }
  }

  const handleFileInputClick = () => {
    if (
      typeof fileInputRef !== 'undefined' &&
      fileInputRef !== null &&
      typeof fileInputRef.current !== 'undefined' &&
      fileInputRef.current !== null
    )
      fileInputRef.current.click()
  }

  const handleCameraInputClick = async () => {
    const isIOS =
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)

    // iOS WebKit only honors `.click()` on a file input within the same
    // synchronous tick as the originating tap - any await before it (e.g.
    // getUserMedia) breaks user-gesture continuity and the picker won't open.
    if (!isIOS && isRunningInWebview() && navigator.mediaDevices?.getUserMedia) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true })
        stream.getTracks().forEach(track => track.stop())
      } catch (e) {
        console.error('Camera permission request failed', e)
      }
    }

    cameraInputRef.current?.click()
  }

  const handleDeleteConfirm = (img: TowingRecordFile) => {
    setImageToDelete(img)
  }

  const handleDelete = async (img: TowingRecordFile) => {
    if (img.id) {
      setLoading(true)
      await deleteFileFromServer(img.id)
      setLoading(false)
    } else {
      setPendingFiles(prev => {
        const next = new Map(prev)
        next.delete(img.url)
        return next
      })
    }

    setImages(i => i.filter(image => image.url !== img.url))
  }

  useEffect(() => {
    setLoading(true)

    const cancelToken = axios.CancelToken
    const source = cancelToken.source()

    axiosInstance
      .get(`/files/${towingRecordId}`, {
        cancelToken: source.token,
      })
      .then((response: AxiosResponse<TowingRecordFileResponse>) => {
        const images = response.data.data.map(f => {
          const blob = b64toBlob(f.base64, f.mimeType)
          const blobUrl = URL.createObjectURL(blob)
          f.url = blobUrl

          return f
        })
        setImages(images)
      })
      .catch(e => {
        if (axios.isCancel(e)) {
          console.log('Request cancelled', e.message)
        } else {
          console.error('Error fetching images', e)
          setErrorNotification(intl.formatMessage(imagesMessages.fetchFailedTitle), intl.formatMessage(imagesMessages.fetchFailed))
        }
      })
      .finally(() => {
        setLoading(false)
      })

    return () => {
      source.cancel()
    }
  }, [towingRecordId, intl])

  const label = vehicleRegNo
    ? intl.formatMessage(imagesMessages.labelWithRegNo, { vehicleRegNo })
    : intl.formatMessage(imagesMessages.label)

  const hasPendingChanges = images.filter(i => i.id === null).length > 0

  return (
    <>
      <FullscreenModal onClose={onClose} label={label}>
        {imageToDelete && (
          <Modal
            label={intl.formatMessage(imagesMessages.deleteConfirmLabel)}
            message={intl.formatMessage(imagesMessages.deleteConfirmMessage)}
            close={() => {
              setImageToDelete(null)
            }}
            onConfirm={() => {
              handleDelete(imageToDelete)
            }}
            closeLabel={intl.formatMessage(imagesMessages.deleteConfirmClose)}
            border="1px solid"
          />
        )}
        <BigButtonSection>
          <Button
            label={intl.formatMessage(imagesMessages.takePhoto)}
            category="on"
            size="m"
            maxWidth="100%"
            onClick={handleCameraInputClick}
          />
          <Button
            label={intl.formatMessage(imagesMessages.uploadPhoto)}
            onClick={handleFileInputClick}
            category="on"
            size="m"
            maxWidth="100%"
          />
        </BigButtonSection>

        <HiddenInput
          type="file"
          ref={fileInputRef}
          multiple
          accept="image/*"
          onChange={onImageChange}
          id="file-upload"
        />
        <HiddenInput
          type="file"
          ref={cameraInputRef}
          accept="image/*"
          capture="environment"
          onChange={onImageChange}
          id="camera-upload"
        />
        <AutoliittoSeparatorLine marginTop="1rem" marginBottom="1rem" />
        {images.length > 0 && (
          <Section height="auto">
            {images.map(img => (
              <Thumbnail
                src={img.url}
                key={img.id ?? img.url}
                onDelete={() => {
                  handleDeleteConfirm(img)
                }}
              />
            ))}
          </Section>
        )}
        {images.length === 0 && <Text>{intl.formatMessage(imagesMessages.noImages)}</Text>}
        {loading && <Loading />}
        {hasPendingChanges && (
          <>
            <Margin />
            <ButtonContainer floatAtBottomNoMenu>
              <Button
                category="save"
                size="l"
                label={intl.formatMessage(buttonMessages.save)}
                mobileLabel={intl.formatMessage(buttonMessages.save)}
                maxWidth="100%"
                onClick={handleUpload}
              ></Button>
            </ButtonContainer>
          </>
        )}
      </FullscreenModal>
    </>
  )
}

const HiddenInput = styled.input`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  border: 0;
  opacity: 0;
`

const upload = async (data: FormData, towingRecordId: number) => {
  const response = await axiosInstance.post(`/files/upload/${towingRecordId}`, data, {
    headers: {
      'Content-Type': 'multipart/form-data'
    },
  })

  return response
}

const deleteFileFromServer = async (id: number) => {
  return await axiosInstance.delete(`/files/${id}`)
}

const handleResponse = (response: AxiosResponse<any>, intl: IntlShape) => {
  switch (response.status) {
    case 401:
      return setUnauthorizedNotification()
    case 413:
      return setErrorNotification(
        intl.formatMessage(imagesMessages.uploadErrorTitle),
        intl.formatMessage(imagesMessages.fileTooLarge),
      )
    case 200:
      return setSuccessNotification(
        intl.formatMessage(imagesMessages.uploadSuccessTitle),
        intl.formatMessage(imagesMessages.uploadSuccess),
      )
    default:
      return setGenericErrorNotification(
        intl.formatMessage(imagesMessages.uploadErrorTitle),
        intl.formatMessage(imagesMessages.uploadFailed),
      )
  }
}

const b64toBlob = (b64Data: string, contentType = '', sliceSize = 512) => {
  const byteCharacters = atob(b64Data)
  const byteArrays = []

  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    const slice = byteCharacters.slice(offset, offset + sliceSize)

    const byteNumbers = new Array(slice.length)
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i)
    }

    const byteArray = new Uint8Array(byteNumbers)
    byteArrays.push(byteArray)
  }

  const blob = new Blob(byteArrays, { type: contentType })
  return blob
}

const Margin = styled.div`
  margin-top: calc(72px + 1rem);
`
